import { DateFormatUtils, LoopButton, Modal, Toast, Typography } from '@loophealth/loop-ui-web-library';
import { AnimatePresence } from 'framer-motion';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveRequiredUserAttributes } from '../../../../adapters';
import { GirlWithPhoneIllustration, OnboardingFtuxIllustrationFour } from '../../../../assets/img';
import { CheckMarkOutlined } from '../../../../assets/img/CustomIcons/CheckMarkOutlined';
import WavyCircle from '../../../../assets/img/CustomIcons/WavyCircle';
import { fetchUserPolicyList } from '../../../../redux/slices/PolicyListSlice';
import WithThemeProvider from '../../../../theme/WithThemeProvider';
import LoopTopLeftLogo from '../../../atoms/LoopTopLeftLogo';
import { selectUserAttributes, selectUserData } from '../../../pages/LoginContainer/redux/slices/UserSlice/selectors';
import { getUserAttributes } from '../../../pages/LoginContainer/redux/slices/UserSlice/thunks';
import {
    StyledUserAttributeHrBannerImg,
    StyledUserAttributeHrModal,
    StyledUserAttributeImageWrapper,
    StyledUserAttributesBody,
    StyledUserAttributesContent,
    StyledUserAttributesHrButton,
    StyledUserAttributesHrText,
    StyledUserAttributesImage,
    StyledUserAttributesItem,
    StyledUserAttributesModal,
    StyledUserAttributesSuccessIcon,
    StyledUserAttributesSuccessIconWrapper,
    StyledUserAttributesSuccessScreen
} from './styles';
import UserAttributesConditions from './UserAttributesConditions';
import UserAttributesFields from './UserAttributesFields';
import LoopTopRightUserMenu from '../../../atoms/LoopTopRightUserMenu';

interface IUserAttributesModalProps {
    setIsVisible: (isVisible: boolean) => void;
}

const UserAttributesModal: React.FC<IUserAttributesModalProps> = ({ setIsVisible }) => {
    const toast = Toast.useToast();
    const dispatch = useDispatch();
    const user = useSelector(selectUserData).data;
    const userAttributes = useSelector(selectUserAttributes);

    const [values, setValues] = useState(userAttributes.data?.userAttributes ?? {});
    const [selectedConditions, setSelectedConditions] = useState(userAttributes.data?.userConditions ?? []);
    const [isLoading, setIsLoading] = React.useState(false);
    const [showSuccessScreen, setShowSuccessScreen] = React.useState(false);
    const [showHrModal, setShowHrModal] = React.useState(false);

    const userId = user?.userId;

    const isSubmitEnabled = useMemo(
        () => userAttributes.data?.attributes.every((a) => !!values[a.attributeName]),
        [values, selectedConditions]
    );

    const onSubmitButtonClick = () => {
        const isHRFieldChanged = userAttributes.data?.attributes
            .filter((a) => a.isHrFilled)
            .some((a) => {
                const currentValue = values[a.attributeName];
                const initialValue = userAttributes.data?.userAttributes?.[a.attributeName];
                return initialValue && currentValue !== initialValue;
            });
        if (isHRFieldChanged) setShowHrModal(true);
        else handleSubmit();
    };

    const handleSubmit = async () => {
        setShowHrModal(false);
        setIsLoading(true);
        const valuesToSave = Object.entries(values).reduce((acc, [k, v]) => {
            if (v instanceof Date) v = new DateFormatUtils().excelToBackendDateFormat(v.toISOString());
            acc[k] = String(v);
            return acc;
        }, {});

        try {
            await saveRequiredUserAttributes(userId, valuesToSave, selectedConditions);
            dispatch(getUserAttributes({ userId }));
            dispatch(fetchUserPolicyList({ userId }));
            setShowSuccessScreen(true);
            setTimeout(() => {
                setIsVisible(false);
            }, 3000);
        } catch (e) {
            toast?.error((e as Error).message ?? 'Something went wrong');
        }
        setIsLoading(false);
    };
    return (
        <>
            <Modal
                isVisible={true}
                setIsVisible={setIsVisible}
                isModalClosable={false}
                closeOnBlur={false}
                disablePadding
            >
                <WithThemeProvider>
                    <StyledUserAttributesModal
                        initial={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 200 }}
                        data-testid="user-attributes-modal"
                    >
                        <LoopTopLeftLogo />
                        <LoopTopRightUserMenu />
                        <StyledUserAttributeImageWrapper layoutId="login-panel-container">
                            <StyledUserAttributesImage
                                src={OnboardingFtuxIllustrationFour}
                                alt="User Attributes"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1, transition: { delay: 0.2 } }}
                            />
                        </StyledUserAttributeImageWrapper>
                        <StyledUserAttributesBody
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1, transition: { delay: 0.2 } }}
                        >
                            <AnimatePresence>
                                {showSuccessScreen ? (
                                    <StyledUserAttributesSuccessScreen
                                        key="success-message"
                                        initial={{ opacity: 0, y: 200 }}
                                        animate={{ opacity: 1, y: 0, transition: { delay: 0.35 } }}
                                        exit={{ opacity: 0 }}
                                    >
                                        <StyledUserAttributesSuccessIconWrapper>
                                            <WavyCircle width="140" height="140" />
                                            <StyledUserAttributesSuccessIcon>
                                                <CheckMarkOutlined width="72" height="72" />
                                            </StyledUserAttributesSuccessIcon>
                                        </StyledUserAttributesSuccessIconWrapper>
                                        <StyledUserAttributesItem>
                                            <Typography variant="title2" weight="medium">
                                                Thank You for Sharing Your Details
                                            </Typography>
                                            <Typography variant="small">
                                                This allows us to tailor your journey and improve your experience.
                                            </Typography>
                                        </StyledUserAttributesItem>
                                    </StyledUserAttributesSuccessScreen>
                                ) : (
                                    <StyledUserAttributesContent
                                        key="form-content"
                                        initial={{ opacity: 1, y: 0 }}
                                        exit={{ y: -200, opacity: 0 }}
                                    >
                                        <StyledUserAttributesItem>
                                            <Typography variant="title2" weight="medium">
                                                You&apos;re Almost There!
                                            </Typography>
                                            <Typography variant="small">
                                                Let us capture some quick details to set up your benefits. Some details
                                                may be pre-filled — you can review and update it.
                                            </Typography>
                                        </StyledUserAttributesItem>
                                        <StyledUserAttributesItem data-testid="user-attributes-marital-status">
                                            <UserAttributesFields values={values} setValues={setValues} />
                                        </StyledUserAttributesItem>
                                        <StyledUserAttributesItem data-testid="user-attributes-marital-status">
                                            <UserAttributesConditions
                                                selectedConditions={selectedConditions}
                                                setSelectedConditions={setSelectedConditions}
                                            />
                                        </StyledUserAttributesItem>
                                        <StyledUserAttributesItem data-testid="user-attributes-submit">
                                            <LoopButton
                                                onClick={onSubmitButtonClick}
                                                variant={isSubmitEnabled ? 'filled' : 'disabled'}
                                                isLoading={isLoading || userAttributes?.loading}
                                                width="fit-content"
                                            >
                                                Save & Proceed
                                            </LoopButton>
                                        </StyledUserAttributesItem>
                                    </StyledUserAttributesContent>
                                )}
                            </AnimatePresence>
                        </StyledUserAttributesBody>
                    </StyledUserAttributesModal>
                </WithThemeProvider>
            </Modal>
            <Modal isVisible={showHrModal} setIsVisible={setShowHrModal} disablePadding closeOnBlur={false}>
                <WithThemeProvider>
                    <StyledUserAttributeHrModal>
                        <StyledUserAttributeHrBannerImg src={GirlWithPhoneIllustration} />
                        <StyledUserAttributesHrText>
                            <Typography variant="large" weight="medium">
                                We’ve Got Your Details
                            </Typography>
                            <Typography variant="extraSmall" weight="medium" color="secondary">
                                Any edits to your pre-filled details will be checked with your HR and confirmed after
                                verification.
                            </Typography>
                        </StyledUserAttributesHrText>
                        <StyledUserAttributesHrButton>
                            <LoopButton variant="filled" onClick={handleSubmit}>
                                Got it
                            </LoopButton>
                        </StyledUserAttributesHrButton>
                    </StyledUserAttributeHrModal>
                </WithThemeProvider>
            </Modal>
        </>
    );
};

export default UserAttributesModal;
